import React, { useState, useEffect } from 'react';
import { Form, Avatar, Select, Tooltip, theme, Row, Col, Space } from 'antd';
import { t } from 'i18next';
import { EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import { CustomerInformationContentStyles } from './CustomerInformationContent.styles';
import { SiteAddressData, ContactPersonData } from '@/store/SiteSurveyForm/constants';
import ArrowsLeftRightIcon from '@/comp/common/icons/ArrowsLeftRightIcon';
import DynamicEditModal from './DynamicEditModal';
import InfoCard from './InfoCard';
import { MODAL_CONFIGS } from './modalConfigs';
import {
  formatAddressContent,
  initializeFormFields,
  CustomerInformationContentProps,
  setDynamicTierOptions,
} from './CustomerInformationContent.utils';
import { observer } from 'mobx-react';
import { useStores } from '@/hooks/use-stores';
import { REGION_KEYS } from '../AccountSelectionStep/Homecontent.utils';
import { AccountSelectFreeSolo } from './components/AccountSelectFreeSolo';

const { Option } = Select;

const { useToken } = theme;

const CustomerInformationContent: React.FC<Omit<CustomerInformationContentProps, 'onChange'>> = observer(
  ({ siteSurveyStore, formId, form }: CustomerInformationContentProps) => {
    const { token } = useToken();
    const { userStore } = useStores();
    const [addressModalVisible, setAddressModalVisible] = useState(false);
    const [accountManagerModalVisible, setAccountManagerModalVisible] = useState(false);
    const [formInitialized, setFormInitialized] = useState(false);
    const isApac = siteSurveyStore.selectedRegion === REGION_KEYS.APAC;

    const activeForm = form;

    useEffect(() => {
      siteSurveyStore.fetchUserData();
    }, []);

    useEffect(() => {
      if (formId && !siteSurveyStore.getFormId()) {
        siteSurveyStore.setFormId(formId);
      }
    }, [formId, siteSurveyStore]);

    useEffect(() => {
      if (!formInitialized && activeForm) {
        const currentTier = activeForm.getFieldValue('tier');
        const currentAccount = activeForm.getFieldValue('account');

        if (!currentTier && !currentAccount) {
          initializeFormFields(activeForm, siteSurveyStore);
        }
        setFormInitialized(true);
      }
    }, [activeForm, siteSurveyStore.customerInformationData, formInitialized]);

    const showAddressModal = (): void => {
      setAddressModalVisible(true);
    };

    const handleAddressModalClose = (): void => {
      setAddressModalVisible(false);
    };

    const showAccountManagerModal = (): void => {
      setAccountManagerModalVisible(true);
    };

    const handleAccountManagerModalClose = (): void => {
      setAccountManagerModalVisible(false);
    };

    const handleAddressSave = (): void => {
      const addressData = activeForm?.getFieldValue('siteAddress');
      if (addressData) {
        siteSurveyStore.updateCustomerInformationData({ siteAddress: addressData });
      }
    };

    const handleAccountManagerSave = (): void => {
      const accountManagerData = activeForm?.getFieldValue('accountManager');
      if (accountManagerData) {
        siteSurveyStore.updateCustomerInformationData({ accountManager: accountManagerData });
      }
    };

    const renderAddressContent = (addressData: SiteAddressData): React.ReactNode => {
      const formattedAddress = formatAddressContent(addressData);

      return (
        <>
          <Tooltip title={formattedAddress.streetAddress}>
            <div className="truncated-text">{formattedAddress.streetAddress}</div>
          </Tooltip>
          <div className="address-details-row">
            <Tooltip title={formattedAddress.cityState}>
              <div className="truncated-text">{formattedAddress.cityState}</div>
            </Tooltip>
            {!formattedAddress.state && (
              <Tooltip title={formattedAddress.country}>
                <div className="truncated-text">{formattedAddress.country}</div>
              </Tooltip>
            )}
            <Tooltip title={formattedAddress.zipCode}>
              <div className="truncated-text">{formattedAddress.zipCode}</div>
            </Tooltip>
            {formattedAddress.state && (
              <Tooltip title={formattedAddress.country}>
                <div className="truncated-text">{formattedAddress.country}</div>
              </Tooltip>
            )}
          </div>
        </>
      );
    };

    const renderAccountManagerContent = (accountManagerData: ContactPersonData): React.ReactNode => {
      return <span>{accountManagerData.fullName}</span>;
    };

    const dynamicTierOptions = setDynamicTierOptions(userStore.whiteLabelingTerms);

    return (
      <CustomerInformationContentStyles theme={{ token }}>
        <div className="form-header">{t('confirmCustomerDetails')}</div>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            {!isApac && (
              <div className="customer-name">{siteSurveyStore.customerInformationData.customer?.name || ''}</div>
            )}
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              {isApac ? (
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <AccountSelectFreeSolo form={form} />
                </Col>
              ) : (
                <Col xs={24} sm={24} md={14} lg={16} xl={18}>
                  <div className="account-id-wrapper">
                    <span className="account-id-label">{t('Salesforce account ID')}</span>
                    <span className="account-id-value">
                      {siteSurveyStore.customerInformationData.customer?.accountId || ''}
                    </span>
                  </div>
                </Col>
              )}
              <Col xs={24} sm={24} md={10} lg={8} xl={6}>
                <Form.Item
                  name="tier"
                  className="tier-form-item"
                  rules={[{ required: true, message: t('Please select a tier') }]}
                >
                  <Select className="tier-select" placeholder={t('selectTier')} size="large" allowClear={false}>
                    {dynamicTierOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {t(option.label)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <InfoCard
                icon={<EnvironmentOutlined />}
                title="siteAddress"
                onEdit={showAddressModal}
                content={
                  siteSurveyStore.customerInformationData.siteAddress &&
                  renderAddressContent(siteSurveyStore.customerInformationData.siteAddress)
                }
                hasData={!!siteSurveyStore.customerInformationData.siteAddress}
              />
            </Col>

            <Col xs={24} md={12}>
              <InfoCard
                icon={<UserOutlined />}
                title="Account Manager"
                onEdit={showAccountManagerModal}
                content={
                  siteSurveyStore.customerInformationData.accountManager &&
                  renderAccountManagerContent(siteSurveyStore.customerInformationData.accountManager)
                }
                hasData={!!siteSurveyStore.customerInformationData.accountManager}
              />
            </Col>
          </Row>

          {!isApac && (
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={24} md={2} lg={2} xl={1}>
                <Avatar className="icon-avatar" icon={<ArrowsLeftRightIcon />} />
              </Col>
              <Col xs={24} sm={24} md={22} lg={14} xl={15}>
                <div className="association-text-container">
                  <div className="association-title">{t('associateWithCustomer')}</div>
                  <div className="association-description">{t('allocateStationDescription')}</div>
                </div>
              </Col>
              <Col xs={24} sm={24} md={{ span: 22, offset: 2 }} lg={{ span: 8, offset: 0 }} xl={{ span: 8, offset: 0 }}>
                <Form.Item name="account" className="account-form-item">
                  <Select
                    className="account-select"
                    placeholder={t('selectAccount')}
                    allowClear
                    loading={siteSurveyStore.isLoading}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {siteSurveyStore.userData && siteSurveyStore.userData.length > 0 ? (
                      siteSurveyStore.userData.map(customer => (
                        <Option key={customer.customerId} value={customer.customerId}>
                          {customer.customerName}
                        </Option>
                      ))
                    ) : (
                      <Option disabled value="">
                        {t('No accounts available')}
                      </Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Space>

        <DynamicEditModal
          visible={addressModalVisible}
          onClose={handleAddressModalClose}
          onSave={handleAddressSave}
          title={MODAL_CONFIGS.ADDRESS.title}
          description={MODAL_CONFIGS.ADDRESS.description}
          fields={MODAL_CONFIGS.ADDRESS.fields[siteSurveyStore.selectedRegion]}
          fieldNamePath={['siteAddress']}
        />

        <DynamicEditModal
          visible={accountManagerModalVisible}
          onClose={handleAccountManagerModalClose}
          onSave={handleAccountManagerSave}
          title={MODAL_CONFIGS.ACCOUNT_MANAGER.title}
          description={MODAL_CONFIGS.ACCOUNT_MANAGER.description}
          fields={MODAL_CONFIGS.ACCOUNT_MANAGER.fields}
          fieldNamePath={['accountManager']}
        />
      </CustomerInformationContentStyles>
    );
  },
);

export default CustomerInformationContent;
